import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PRACTICE_AREAS, getPracticeArea } from "@/data/practiceAreas";
import { api } from "@/lib/api";

const GREETING =
  "Hi, I'm here to help you find the right AMADI contact. How can I help?";
const DISCLAIMER =
  "This assistant provides general information only and does not constitute legal advice.";
const SAFE_FALLBACK =
  "That's a great question for a consultation — I can help you book one, or you can reach us directly.";

const MAIN_OPTIONS = [
  { id: "practice", label: "Which practice area fits my need?" },
  { id: "book", label: "Book a consultation" },
  { id: "general", label: "General question" },
];

const PRACTICE_OPTIONS = [
  { id: "p1", label: "Starting or structuring a business", slug: "corporate-ma" },
  { id: "p2", label: "Raising or moving capital", slug: "banking-finance" },
  { id: "p3", label: "Regulatory or compliance (AML/CFT)", slug: "regulatory-compliance" },
  { id: "p4", label: "Employment or team matters", slug: "employment-incentives" },
  { id: "p5", label: "A dispute or restructuring", slug: "restructuring-disputes" },
  { id: "p6", label: "Expanding across borders / Africa", slug: "cross-border-africa" },
  { id: "p7", label: "Something else", slug: null },
];

const GENERAL_OPTIONS = [
  { id: "g-areas", label: "What practice areas do you cover?" },
  { id: "g-location", label: "Where are your offices?" },
  { id: "g-book", label: "How do I book?" },
  { id: "g-response", label: "What are your response times?" },
];

const MENU_OPTION = { id: "menu", label: "Back to start" };
const LEAVE_OPTION = { id: "leave", label: "Leave my details & we'll reach out" };

const botMsg = (text, links = null) => ({ from: "bot", text, links });
const userMsg = (text) => ({ from: "user", text, links: null });

export const ChatWidget = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState(MAIN_OPTIONS);
  const [input, setInput] = useState("");
  const [capture, setCapture] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadSending, setLeadSending] = useState(false);
  const [leadError, setLeadError] = useState("");
  const scrollRef = useRef(null);

  // Seed the greeting the first time the panel opens.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([botMsg(GREETING)]);
      setOptions(MAIN_OPTIONS);
    }
  }, [open, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, options]);

  const push = (...msgs) => setMessages((prev) => [...prev, ...msgs]);

  const handleOption = (opt) => {
    push(userMsg(opt.label));

    if (opt.id === "practice") {
      push(botMsg("Happy to point you in the right direction. What's this about?"));
      setOptions(PRACTICE_OPTIONS);
      return;
    }
    if (opt.id === "book") {
      push(
        botMsg("Wonderful — you can share a few details and we'll be in touch.", [
          { label: "Go to the contact form", to: "/contact" },
        ])
      );
      setOptions([LEAVE_OPTION, MENU_OPTION]);
      return;
    }
    if (opt.id === "general") {
      push(botMsg("Of course — here's what I can help with. Pick one, or type your question below."));
      setOptions([...GENERAL_OPTIONS, LEAVE_OPTION]);
      return;
    }

    if (opt.id === "leave") {
      setLeadError("");
      setCapture(true);
      push(botMsg("Sure — leave your details below and someone from AMADI will be in touch. Your email is all we need to get started."));
      setOptions([]);
      return;
    }

    // Practice-area routing
    if (opt.id?.startsWith("p")) {
      if (opt.slug) {
        const area = getPracticeArea(opt.slug);
        push(
          botMsg(
            `That sounds like our ${area.title} practice. Here's an overview — and you're always welcome to book a consultation.`,
            [
              { label: `View ${area.title}`, to: `/practice/${opt.slug}` },
              { label: "Book a consultation", to: "/contact" },
            ]
          )
        );
      } else {
        push(
          botMsg(
            "No problem — the quickest way is to send us a short note and we'll route you to the right person.",
            [{ label: "Go to the contact form", to: "/contact" }]
          )
        );
      }
      setOptions([LEAVE_OPTION, MENU_OPTION]);
      return;
    }

    // General firm-level info (scripted only)
    if (opt.id === "g-areas") {
      push(
        botMsg(
          `We advise across ten focused practices: ${PRACTICE_AREAS.map((p) => p.title).join(", ")}.`,
          [{ label: "See all practice areas", to: "/#practice-areas" }]
        )
      );
      setOptions([...GENERAL_OPTIONS.filter((o) => o.id !== "g-areas"), LEAVE_OPTION, MENU_OPTION]);
      return;
    }
    if (opt.id === "g-location") {
      push(botMsg("We work across Nairobi, Dubai, Cape Town, and Lagos, advising clients throughout the UAE, the Middle East, and Africa."));
      setOptions([...GENERAL_OPTIONS.filter((o) => o.id !== "g-location"), LEAVE_OPTION, MENU_OPTION]);
      return;
    }
    if (opt.id === "g-book") {
      push(
        botMsg("You can book anytime using the contact form — just share a few details about your matter.", [
          { label: "Go to the contact form", to: "/contact" },
        ])
      );
      setOptions([...GENERAL_OPTIONS.filter((o) => o.id !== "g-book"), LEAVE_OPTION, MENU_OPTION]);
      return;
    }
    if (opt.id === "g-response") {
      push(botMsg("We aim to respond to every enquiry promptly, typically within one to two business days."));
      setOptions([...GENERAL_OPTIONS.filter((o) => o.id !== "g-response"), LEAVE_OPTION, MENU_OPTION]);
      return;
    }

    if (opt.id === "menu") {
      push(botMsg("Sure — how else can I help?"));
      setOptions(MAIN_OPTIONS);
    }
  };

  const handleSend = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    push(userMsg(text));
    setInput("");
    // Scripted-only: never attempt substantive/legal answers.
    push(
      botMsg(SAFE_FALLBACK, [
        { label: "Book a consultation", to: "/contact" },
      ])
    );
    setOptions([LEAVE_OPTION, MENU_OPTION]);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadError("");
    const email = lead.email.trim();
    if (!email) {
      setLeadError("Please add an email so we can reach you.");
      return;
    }
    setLeadSending(true);
    try {
      await api.post("/api/enquiries", {
        email,
        name: lead.name.trim(),
        message: lead.message.trim(),
        source: "chatbot",
      });
      setCapture(false);
      setLead({ name: "", email: "", message: "" });
      push(botMsg("Thank you — we've noted your details and someone from AMADI will be in touch shortly."));
      setOptions([MENU_OPTION]);
    } catch {
      setLeadError("Something went wrong. Please try again, or use our contact form.");
    } finally {
      setLeadSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <motion.button
        type="button"
        data-testid="chat-widget-button"
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 260, damping: 20 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[60] inline-flex items-center justify-center w-14 h-14 rounded-full bg-forest text-canvas shadow-lg shadow-charcoal/20 border border-forest hover:bg-charcoal transition-colors duration-300"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="w-5 h-5" strokeWidth={1.6} />
            </motion.span>
          ) : (
            <motion.span key="msg" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageSquare className="w-5 h-5" strokeWidth={1.6} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[380px] max-h-[70vh] flex flex-col bg-canvas border border-charcoal/15 shadow-2xl shadow-charcoal/25 overflow-hidden"
            role="dialog"
            aria-label="AMADI assistant"
          >
            {/* Header */}
            <div className="bg-forest text-canvas px-5 py-4 flex items-center justify-between shrink-0">
              <div>
                <p className="font-serif text-base leading-tight">AMADI Assistant</p>
                <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-gold mt-0.5">
                  Here to point you the right way
                </p>
              </div>
              <button
                type="button"
                data-testid="chat-widget-close"
                aria-label="Close chat"
                onClick={() => setOpen(false)}
                className="text-canvas/80 hover:text-gold transition-colors duration-300"
              >
                <X className="w-4 h-4" strokeWidth={1.6} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.from}-${i}`}
                    className={`max-w-[85%] px-4 py-2.5 font-serif text-sm leading-relaxed ${
                      m.from === "user"
                        ? "bg-forest text-canvas"
                        : "bg-white text-charcoal border border-charcoal/10"
                    }`}
                  >
                    {m.text}
                    {m.links && (
                      <div className="mt-3 flex flex-col gap-2">
                        {m.links.map((lnk, li) => (
                          <button
                            key={li}
                            type="button"
                            data-testid={`chat-link-${i}-${li}`}
                            onClick={() => {
                              setOpen(false);
                              navigate(lnk.to);
                            }}
                            className="group inline-flex items-center justify-between gap-2 font-sans text-[11px] tracking-[0.12em] uppercase font-bold px-3 py-2 bg-charcoal text-canvas hover:bg-gold hover:text-charcoal transition-colors duration-300"
                          >
                            {lnk.label}
                            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.6} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Quick replies */}
              {options.length > 0 && (
                <div className="flex flex-col items-start gap-2 pt-1">
                  {options.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`chat-quick-${opt.id}`}
                      onClick={() => handleOption(opt)}
                      className="text-left font-sans text-[12px] tracking-[0.02em] px-3.5 py-2 border border-forest/40 text-forest hover:bg-forest hover:text-canvas transition-colors duration-300"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Lead capture form */}
              {capture && (
                <form onSubmit={submitLead} data-testid="chat-capture-form" className="bg-white border border-charcoal/10 p-4 space-y-2.5">
                  <input
                    data-testid="chat-capture-name"
                    type="text"
                    value={lead.name}
                    onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                    placeholder="Your name (optional)"
                    className="w-full bg-transparent border border-charcoal/20 px-3 py-2 font-serif text-sm text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:border-forest transition-colors duration-300"
                  />
                  <input
                    data-testid="chat-capture-email"
                    type="email"
                    required
                    value={lead.email}
                    onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                    placeholder="Email address"
                    className="w-full bg-transparent border border-charcoal/20 px-3 py-2 font-serif text-sm text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:border-forest transition-colors duration-300"
                  />
                  <textarea
                    data-testid="chat-capture-message"
                    rows={2}
                    value={lead.message}
                    onChange={(e) => setLead((l) => ({ ...l, message: e.target.value }))}
                    placeholder="How can we help? (optional)"
                    className="w-full bg-transparent border border-charcoal/20 px-3 py-2 font-serif text-sm text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:border-forest transition-colors duration-300 resize-none"
                  />
                  {leadError && (
                    <p data-testid="chat-capture-error" className="font-sans text-[11px] text-red-700">
                      {leadError}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      data-testid="chat-capture-submit"
                      disabled={leadSending}
                      className="flex-1 font-sans text-[11px] tracking-[0.12em] uppercase font-bold px-3 py-2.5 bg-forest text-canvas hover:bg-gold hover:text-charcoal transition-colors duration-300 disabled:opacity-60"
                    >
                      {leadSending ? "Sending…" : "Send my details"}
                    </button>
                    <button
                      type="button"
                      data-testid="chat-capture-cancel"
                      onClick={() => {
                        setCapture(false);
                        setLeadError("");
                        setOptions([MENU_OPTION]);
                      }}
                      className="font-sans text-[11px] tracking-[0.12em] uppercase px-3 py-2.5 border border-charcoal/20 text-charcoal/60 hover:border-forest hover:text-forest transition-colors duration-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Disclaimer */}
            <p data-testid="chat-disclaimer" className="shrink-0 px-4 py-2.5 bg-[#F1F1EE] border-t border-charcoal/10 font-sans text-[10px] leading-snug text-charcoal/55">
              {DISCLAIMER}
            </p>

            {/* Input */}
            <form onSubmit={handleSend} className="shrink-0 flex items-center gap-2 px-3 py-3 border-t border-charcoal/10 bg-canvas">
              <input
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message…"
                aria-label="Type a message"
                className="flex-1 bg-transparent border border-charcoal/20 px-3 py-2.5 font-serif text-sm text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:border-forest transition-colors duration-300"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                aria-label="Send message"
                className="inline-flex items-center justify-center w-10 h-10 bg-forest text-canvas hover:bg-gold hover:text-charcoal transition-colors duration-300 shrink-0"
              >
                <Send className="w-4 h-4" strokeWidth={1.6} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
